// Notification acknowledgement is separate from transport arrival counts. A pending basis stays
// pending across refreshes until the server exposes its current draft or actionable exception.
import type { RequestListItem } from "@/api";
import { TOAST_ACTION_MS } from "./toast-queue";

type ArrivalRow = Pick<
  RequestListItem,
  | "id"
  | "request_no"
  | "subject"
  | "from"
  | "inbound_message_count"
  | "new_customer_inbound_message_index"
  | "incoming_work"
>;

/** Request -> ready basis and attention transitions already observed in this tenant scope. */
export type ArrivalSnapshot = Map<string, { readyBasis: number; attentionIds: ReadonlySet<string> }>;

/** Arrivals within this window aggregate into one notice instead of stacking toasts. */
export const ARRIVAL_MERGE_WINDOW_MS = TOAST_ACTION_MS;

export interface InboundArrival {
  notificationId: string;
  id: string;
  /** What the router opens. The inbox links by request number when the request has one. */
  routeId: string;
  subject: string | null;
  from: string | null;
  kind: "request" | "reply";
  category: "ready" | "attention";
  sound: "signature" | "message-dry";
  /** Toast suppression never suppresses this arrival's audio event. */
  toastSuppressed: boolean;
}

/** The route the inbox uses for a row; the workspace resolves either form. */
export function arrivalRouteId(row: Pick<ArrivalRow, "id" | "request_no">): string {
  return row.request_no === null || row.request_no === undefined ? row.id : String(row.request_no);
}

export function buildArrivalSnapshot(rows: readonly ArrivalRow[], previous?: ArrivalSnapshot | null): ArrivalSnapshot {
  const next = new Map(previous ?? []);
  for (const row of rows) {
    const before = next.get(row.id);
    const notice = row.incoming_work?.notification;
    // Initial display is silent; a request still processing can announce later readiness.
    const basis = row.incoming_work?.inbound_basis ?? 0;
    const readyBasis = notice?.kind === "ready" || row.incoming_work?.state === "settled" ? basis : 0;
    const attentionIds = new Set(before?.attentionIds ?? []);
    if (notice?.kind === "attention") attentionIds.add(notice.id);
    next.set(row.id, { readyBasis: Math.max(before?.readyBasis ?? 0, readyBasis), attentionIds });
  }
  return next;
}

/** True when this row is the request the operator already has open. */
function isOnScreen(row: ArrivalRow, openRouteId: string | null): boolean {
  if (!openRouteId) return false;
  if (openRouteId === row.id) return true;
  return row.request_no !== null && row.request_no !== undefined && openRouteId === String(row.request_no);
}

/**
 * One event per newly reviewable request/basis, even when it covers several inbound messages.
 * Manual regeneration on that basis is independently unseen, but is never another mail arrival.
 */
export function detectInboundArrivals(
  previous: ArrivalSnapshot,
  rows: readonly ArrivalRow[],
  openRouteId: string | null = null,
): InboundArrival[] {
  const arrivals: InboundArrival[] = [];
  for (const row of rows) {
    const seen = previous.get(row.id);
    const work = row.incoming_work;
    const notice = work?.notification;
    if (!work || !notice) continue;
    if (notice.kind === "ready" && work.inbound_basis <= (seen?.readyBasis ?? 0)) continue;
    if (notice.kind === "attention" && seen?.attentionIds.has(notice.id)) continue;
    const toastSuppressed = isOnScreen(row, openRouteId);
    arrivals.push({
      notificationId: notice.id,
      id: row.id,
      routeId: arrivalRouteId(row),
      subject: row.subject,
      from: row.from,
      kind: work.inbound_basis === 1 ? "request" : "reply",
      category: notice.kind,
      sound: row.new_customer_inbound_message_index != null
        && row.new_customer_inbound_message_index > (seen?.readyBasis ?? 0)
        && row.new_customer_inbound_message_index <= work.inbound_basis ? "signature" : "message-dry",
      toastSuppressed,
    });
  }
  return arrivals;
}

export interface ArrivalNotice {
  notificationId: string | null;
  category: "ready" | "attention";
  count: number;
  /** The request to open, or null when several arrived and the inbox is the right target. */
  routeId: string | null;
  subject: string | null;
  from: string | null;
  kind: "request" | "reply";
  expiresAt: number;
}

/**
 * Folds new arrivals into the notice still on screen. A second mail while the first toast lives
 * turns it into "2 new requests" rather than a second toast; once the window passes, counting
 * starts again from the new arrival.
 */
export function mergeArrivalNotice(
  existing: ArrivalNotice | null,
  arrivals: readonly InboundArrival[],
  now: number,
  windowMs: number = ARRIVAL_MERGE_WINDOW_MS,
): ArrivalNotice | null {
  if (arrivals.length === 0) return null;
  const live = existing && existing.expiresAt > now ? existing : null;
  const count = (live?.count ?? 0) + arrivals.length;
  const expiresAt = now + windowMs;
  if (count === 1) {
    const [only] = arrivals;
    return { count, routeId: only.routeId, subject: only.subject, from: only.from, kind: only.kind, category: only.category, notificationId: only.notificationId, expiresAt };
  }
  return { count, routeId: null, subject: null, from: null, kind: "request", notificationId: null, category: arrivals.some(a => a.category === "attention") || live?.category === "attention" ? "attention" : "ready", expiresAt };
}
